export const MIN_PASSWORD_LENGTH = 4
export const MAX_PASSWORD_LENGTH = 128
export const DEFAULT_PASSWORD_LENGTH = 20
export const LENGTH_COMMIT_DELAY_MS = 100

const AMBIGUOUS_CHARACTERS = new Set('0O1Il')

export type CharacterSetKey = 'uppercase' | 'lowercase' | 'numbers' | 'symbols'

export interface PasswordSettings {
  uppercase: boolean
  lowercase: boolean
  numbers: boolean
  symbols: boolean
  excludeAmbiguous: boolean
}

export interface GeneratedPassword {
  value: string
  poolSize: number
  selectedSetCount: number
  entropyBits: number
}

export type PasswordGenerationErrorCode = 'empty-set' | 'crypto-unavailable'

export class PasswordGenerationError extends Error {
  constructor(readonly code: PasswordGenerationErrorCode) {
    super(code)
  }
}

export const CHARACTER_SET_ORDER: CharacterSetKey[] = [
  'lowercase',
  'uppercase',
  'numbers',
  'symbols',
]

const CHARACTER_SETS: Record<CharacterSetKey, string> = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*_-+=?~',
}

export const DEFAULT_PASSWORD_SETTINGS: PasswordSettings = {
  uppercase: true,
  lowercase: true,
  numbers: true,
  symbols: true,
  excludeAmbiguous: false,
}

export function clampPasswordLength(value: number): number {
  return Math.min(MAX_PASSWORD_LENGTH, Math.max(MIN_PASSWORD_LENGTH, value))
}

export function selectedCharacterSets(settings: PasswordSettings): string[] {
  return CHARACTER_SET_ORDER
    .filter((key) => settings[key])
    .map((key) => {
      const source = CHARACTER_SETS[key]
      if (!settings.excludeAmbiguous) return source
      return Array.from(source)
        .filter((character) => !AMBIGUOUS_CHARACTERS.has(character))
        .join('')
    })
    .filter((source) => source.length > 0)
}

function secureRandomIndex(maxExclusive: number): number {
  const cryptoObject = globalThis.crypto as Crypto | undefined
  if (!cryptoObject?.getRandomValues) {
    throw new PasswordGenerationError('crypto-unavailable')
  }

  const limit = 256 - (256 % maxExclusive)
  const bytes = new Uint8Array(1)
  do {
    cryptoObject.getRandomValues(bytes)
  } while (bytes[0]! >= limit)

  return bytes[0]! % maxExclusive
}

export type RandomIndex = (maxExclusive: number) => number

function randomCharacter(source: string, randomIndex: RandomIndex): string {
  return source[randomIndex(source.length)]!
}

function shuffleCharacters(
  characters: string[],
  randomIndex: RandomIndex,
): string[] {
  for (let index = characters.length - 1; index > 0; index--) {
    const swapIndex = randomIndex(index + 1)
    const current = characters[index]!
    characters[index] = characters[swapIndex]!
    characters[swapIndex] = current
  }
  return characters
}

export function generatePassword(
  length: number,
  settings: PasswordSettings,
  randomIndex: RandomIndex = secureRandomIndex,
): GeneratedPassword {
  const sets = selectedCharacterSets(settings)
  if (sets.length === 0) {
    throw new PasswordGenerationError('empty-set')
  }

  const normalizedLength = clampPasswordLength(length)
  const pool = sets.join('')
  const result = sets.map((source) => randomCharacter(source, randomIndex))
  for (let index = result.length; index < normalizedLength; index++) {
    result.push(randomCharacter(pool, randomIndex))
  }

  return {
    value: shuffleCharacters(result, randomIndex).join(''),
    poolSize: pool.length,
    selectedSetCount: sets.length,
    entropyBits: normalizedLength * Math.log2(pool.length),
  }
}

export interface PasswordGeneratorState {
  draftLength: number
  committedLength: number
  settings: PasswordSettings
  generated: GeneratedPassword | null
  copied: boolean
  errorCode: PasswordGenerationErrorCode | null
  generationRevision: number
}

export type PasswordGeneratorAction =
  | { type: 'change-length'; length: number }
  | { type: 'commit-length'; length: number }
  | { type: 'toggle-character-set'; key: CharacterSetKey }
  | { type: 'toggle-ambiguous' }
  | { type: 'generation-succeeded'; generated: GeneratedPassword }
  | { type: 'generation-failed'; errorCode: PasswordGenerationErrorCode }
  | { type: 'copy-succeeded' }
  | { type: 'reset-copy' }

export function createPasswordGeneratorState(): PasswordGeneratorState {
  return {
    draftLength: DEFAULT_PASSWORD_LENGTH,
    committedLength: DEFAULT_PASSWORD_LENGTH,
    settings: DEFAULT_PASSWORD_SETTINGS,
    generated: null,
    copied: false,
    errorCode: null,
    generationRevision: 0,
  }
}

export function passwordGeneratorReducer(
  state: PasswordGeneratorState,
  action: PasswordGeneratorAction,
): PasswordGeneratorState {
  switch (action.type) {
    case 'change-length':
      return {
        ...state,
        draftLength: clampPasswordLength(action.length),
        generated: null,
        copied: false,
        errorCode: null,
      }
    case 'commit-length': {
      const length = clampPasswordLength(action.length)
      return {
        ...state,
        draftLength: length,
        committedLength: length,
        generated: null,
        copied: false,
        errorCode: null,
        generationRevision: state.generationRevision + 1,
      }
    }
    case 'toggle-character-set': {
      const enabledCount = CHARACTER_SET_ORDER.filter((key) => state.settings[key]).length
      if (state.settings[action.key] && enabledCount === 1) {
        return {
          ...state,
          copied: false,
          errorCode: 'empty-set',
        }
      }
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.key]: !state.settings[action.key],
        },
        generated: null,
        copied: false,
        errorCode: null,
        generationRevision: state.generationRevision + 1,
      }
    }
    case 'toggle-ambiguous':
      return {
        ...state,
        settings: {
          ...state.settings,
          excludeAmbiguous: !state.settings.excludeAmbiguous,
        },
        generated: null,
        copied: false,
        errorCode: null,
        generationRevision: state.generationRevision + 1,
      }
    case 'generation-succeeded':
      return {
        ...state,
        generated: action.generated,
        copied: false,
        errorCode: null,
      }
    case 'generation-failed':
      return {
        ...state,
        generated: null,
        copied: false,
        errorCode: action.errorCode,
      }
    case 'copy-succeeded':
      return {
        ...state,
        copied: true,
      }
    case 'reset-copy':
      return {
        ...state,
        copied: false,
      }
  }
}
